import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api, Button, Card, ConfirmDialog, InfoTip, PillButton, apiErrorMessage, brandDisplayName, formatIsoDay, useBrand } from '@aralere/shared-ui';
import type {
  Business,
  CatalogTaxCategory,
  TaxReviewExemptionProblem,
  TaxReviewProfileView,
  TaxReviewResponse,
  TaxReviewRuleView,
  TaxReviewServiceRow,
} from '@aralere/shared-types';
import { CATALOG_TAX_CATEGORIES, TAX_CATEGORY_OPTIONS, TAX_EXEMPTION_REASON_LABELS, TAX_REVIEW_REASON_LABELS, taxCategoryLabel } from './taxCategory';

// THE ANNUAL TAX-CATEGORY REVIEW (04 #160 WS-B; 02i T4 + §8 DP-12). One brand-level screen, COMPUTED ON
// READ by the server (GET mgmt-tax-review): nothing on this page derives a rule, a date or a count - it
// renders the server's answer and offers exactly three actions, each a server call:
//   - stage category changes on the catalog rows (per row "Use suggestion" or the bulk "Set selected to"),
//     then APPLY them in ONE call (PATCH mgmt-services?action=bulk-tax-category) - each changed row takes
//     the editor's own path and trail; nothing is ever changed without the admin's click (02i T4);
//   - COMPLETE the review (POST ?action=complete) - "Complete review", or "Confirm all unchanged" when
//     nothing was changed this visit: the same action, one audit row, the banner trigger acknowledged;
//   - refresh.
// The dashboard banner reads the same server trigger (?view=status); this page is ALSO in the System
// menu all year so a completed review can be re-opened. Brand scope follows the Remittances page: a
// picker that collapses at one brand, `?business=` as the deep link the banner uses.
// Target (02i T4): a brand with 300 items and nothing to change finishes in under a minute.

const inputClass = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';
const chip = 'rounded-full px-2 py-0.5 text-[11px] leading-4';
const warnChip = `${chip} bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]`;
const mutedChip = `${chip} bg-[rgb(var(--edge-rgb)/0.06)] text-[var(--color-muted)]`;
// On a TINTED card a filled chip either vanishes (chip tint == card tint) or reads as a third green, so the status
// card's chips are OUTLINE chips: transparent fill, text-colour border (owner, s231 fix pass).
const warnChipOnTint = `${chip} border border-[var(--tint-warn-text)] bg-transparent text-[var(--tint-warn-text)]`;
const okChipOnTint = `${chip} border border-[var(--tint-green-text)] bg-transparent text-[var(--tint-green-text)]`;
// Thin one-line result bars (the Services.tsx L1161 shape), never a full Card: green for a clean apply or a
// completion, amber when some rows failed.
// The "changes applied this visit" count survives a page reload within the tab (sessionStorage, per brand;
// cleared by completion). Owner-observed at the s231 verification: a reload between two applies reset it to
// 0 and the completion would have recorded 2 instead of 4. Best-effort: storage may be unavailable.
const appliedKey = (businessId: string): string => `taxReview.applied.${businessId}`;
const readApplied = (businessId: string): number => {
  try { const v = Number(sessionStorage.getItem(appliedKey(businessId))); return Number.isInteger(v) && v > 0 ? v : 0; } catch { return 0; }
};
const writeApplied = (businessId: string, n: number): void => {
  try { if (n > 0) sessionStorage.setItem(appliedKey(businessId), String(n)); else sessionStorage.removeItem(appliedKey(businessId)); } catch { /* ignore */ }
};
const okBar = 'flex items-center justify-between gap-3 rounded-md border border-[var(--tint-green-border)] bg-[var(--tint-green-bg-soft)] px-3 py-1.5 text-sm text-[var(--tint-green-text)]';
const warnBar = 'flex items-center justify-between gap-3 rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg)] px-3 py-1.5 text-sm text-[var(--tint-warn-text)]';
const sectionTitle = 'text-xs font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]';

const EXEMPTION_PROBLEM_LABEL: Record<TaxReviewExemptionProblem, string> = {
  'missing-certificate': 'No certificate reference: this exemption does not exempt',
  expired: 'Certificate expired: no longer exempt',
  expiring: 'Certificate expires within 90 days',
  'not-yet-effective': 'Not yet in effect',
};

// 04 #160 WS-D: the reason words come from the one SPA mirror (taxCategory.ts) the client editor uses too.
const EXEMPTION_REASON_LABEL: Record<string, string> = TAX_EXEMPTION_REASON_LABELS;

const pct = (rate: number): string => `${String(Number((rate * 100).toPrecision(12)))}%`;

function ruleLine(r: TaxReviewRuleView): string {
  const what = !r.taxable
    ? 'not taxable'
    : `taxable${r.taxableFraction < 1 ? ` on ${Math.round(r.taxableFraction * 100)}%` : ''}${r.rate !== null ? ` at ${pct(r.rate)}` : ''}`;
  const when = r.effectiveFrom || r.effectiveTo ? ` (${r.effectiveFrom ? `from ${formatIsoDay(r.effectiveFrom)}` : ''}${r.effectiveFrom && r.effectiveTo ? ' ' : ''}${r.effectiveTo ? `through ${formatIsoDay(r.effectiveTo)}` : ''})` : '';
  return `${r.categoryLabel}: ${what}${when}`;
}

function RuleList({ rows, emptyText, showChangelog }: { rows: TaxReviewRuleView[]; emptyText: string; showChangelog?: boolean }): JSX.Element {
  if (rows.length === 0) return <p className="text-xs text-[var(--color-muted)]">{emptyText}</p>;
  return (
    <ul className="space-y-1 text-sm">
      {rows.map((r) => (
        <li key={r.ruleId} className="space-y-0.5">
          <div>
            {ruleLine(r)}
            {r.basis === 'coverage-day' ? <span className={`ml-2 ${mutedChip}`}>by coverage day</span> : null}
            {r.sourcing !== 'destination-billing' ? <span className={`ml-2 ${mutedChip}`}>{r.sourcing === 'destination-ship-to' ? 'ship-to address' : 'seller origin'}</span> : null}
          </div>
          {r.note ? <div className="text-xs text-[var(--color-muted)]">{r.note}</div> : null}
          {showChangelog && r.changelog ? (
            <div className="text-xs text-[var(--color-muted)]">
              Change note ({formatIsoDay(r.changelog.date)}): {r.changelog.summary}
              {r.changelog.source ? ` Source: ${r.changelog.source}.` : ''}
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function ProfileCard({ p }: { p: TaxReviewProfileView }): JSX.Element {
  const [open, setOpen] = useState(p.upcoming.length > 0);
  return (
    <Card>
      <div className="space-y-3 p-2">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{p.stateName}</span>
              <span className="text-xs text-[var(--color-muted)]">{p.id}</span>
              {p.stale ? <span className={warnChip}>Not reviewed by the operator for over a year</span> : null}
              {p.upcoming.length > 0 ? <span className={warnChip}>{p.upcoming.length === 1 ? '1 upcoming change' : `${p.upcoming.length} upcoming changes`}</span> : null}
            </div>
            <div className="text-xs text-[var(--color-muted)]">
              Profile version {p.version ? formatIsoDay(p.version) : 'not dated'} · operator review {p.reviewedAt ? formatIsoDay(p.reviewedAt) : 'not recorded'} · {p.regime === 'vat' ? 'VAT' : 'sales tax'} · package allocation by {p.bundleAllocation === 'member-list-price' ? 'member list price' : p.bundleAllocation}
            </div>
          </div>
          <PillButton onClick={() => setOpen((o) => !o)}>{open ? 'Hide rules' : 'Show rules'}</PillButton>
        </div>
        {open ? (
          <div className="grid gap-4 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-3 md:grid-cols-3">
            <div className="space-y-1">
              <div className={sectionTitle}>In force today</div>
              <RuleList rows={p.inForce} emptyText="No rules in force: every category is untaxed here." />
            </div>
            <div className="space-y-1">
              <div className={sectionTitle}>Upcoming</div>
              <RuleList rows={p.upcoming} emptyText="Nothing scheduled." showChangelog />
              {p.upcomingRates.length > 0 ? (
                <ul className="space-y-1 text-sm">
                  {p.upcomingRates.map((r) => (
                    <li key={`${r.jurisdictionId}-${r.effectiveFrom}`}>{r.jurisdictionName} rate: {pct(r.rate)} from {formatIsoDay(r.effectiveFrom)}</li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="space-y-1">
              <div className={sectionTitle}>Superseded</div>
              <RuleList rows={p.superseded} emptyText="Nothing superseded." />
            </div>
            <div className="md:col-span-3 text-xs text-[var(--color-muted)]">
              Jurisdictions: {p.jurisdictions.map((j) => `${j.name} ${pct(j.rate)}${j.isDefault ? ' (default)' : ''}`).join(' · ')}
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export function AdminTaxReview(): JSX.Element {
  const location = useLocation();
  const { brand } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── brand scope (the Remittances convention) ─────────────────────────────────
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [businessId, setBusinessId] = useState<string>(searchParams.get('business') ?? '');
  const [bizErr, setBizErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { businesses: list } = await api.admin.listBusinesses();
        if (!alive) return;
        setBusinesses(list);
        setBusinessId((cur) => (cur && list.some((b) => b.id === cur) ? cur : list.find((b) => b.id === brand?.id)?.id ?? list[0]?.id ?? ''));
      } catch (e) {
        if (alive) setBizErr(apiErrorMessage(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [brand?.id]);

  const pickBrand = (id: string): void => {
    setBusinessId(id);
    setReview(null);
    setPending({});
    setSelected([]);
    setAppliedCount(readApplied(id));
    const next = new URLSearchParams(searchParams);
    next.set('business', id);
    setSearchParams(next, { replace: true });
  };

  // ── the review (ONE read) ────────────────────────────────────────────────────
  const [review, setReview] = useState<TaxReviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Two results, two homes (owner, s231 fix pass): an APPLY result sits directly above the services cards it
  // describes; a COMPLETION result sits by the status card. Each is one thin line, dismissable.
  const [applyResult, setApplyResult] = useState<{ text: string; failed: boolean } | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (!businessId) return;
    let alive = true;
    setLoading(true);
    setErr(null);
    void (async () => {
      try {
        const res = await api.admin.taxReview(businessId);
        if (alive) setReview(res);
      } catch (e) {
        if (alive) {
          setErr(apiErrorMessage(e));
          setReview(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [businessId, reloadKey, location.key]);

  // ── staged category changes ──────────────────────────────────────────────────
  const [pending, setPending] = useState<Record<string, CatalogTaxCategory>>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [bulkTarget, setBulkTarget] = useState<'' | CatalogTaxCategory>('');
  const [applyBusy, setApplyBusy] = useState(false);
  const [appliedCount, setAppliedCount] = useState(0);
  useEffect(() => { if (businessId) setAppliedCount(readApplied(businessId)); }, [businessId]);

  const services = review?.services ?? [];
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) => [s.name, s.shortName, s.vendor, s.category, taxCategoryLabel(s.effectiveTaxCategory)].some((v) => v.toLowerCase().includes(q)));
  }, [services, search]);
  const grouped = useMemo(() => {
    const m = new Map<CatalogTaxCategory, TaxReviewServiceRow[]>();
    for (const c of CATALOG_TAX_CATEGORIES) m.set(c, []);
    for (const s of shown) m.get(s.effectiveTaxCategory)?.push(s);
    return m;
  }, [shown]);

  /** The category a row would carry after the staged changes: pending, else stored, else none. */
  const stagedOf = (s: TaxReviewServiceRow): CatalogTaxCategory | null => pending[s.id] ?? s.taxCategory;
  const stage = (ids: string[], category: CatalogTaxCategory): void => {
    setPending((cur) => {
      const next = { ...cur };
      for (const id of ids) {
        const row = services.find((s) => s.id === id);
        if (!row) continue;
        if (row.taxCategory === category) delete next[id]; // back to the stored value = nothing to apply
        else next[id] = category;
      }
      return next;
    });
  };
  const unstage = (id: string): void => setPending((cur) => { const next = { ...cur }; delete next[id]; return next; });
  const pendingIds = Object.keys(pending);
  const toggleSelected = (id: string): void => setSelected((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const selectShown = (): void => setSelected(shown.map((s) => s.id));
  const clearSelected = (): void => setSelected([]);
  const suggestible = services.filter((s) => s.suggestion && s.suggestion.category !== stagedOf(s));

  const applyChanges = async (): Promise<void> => {
    if (!businessId || pendingIds.length === 0) return;
    setApplyBusy(true);
    setErr(null);
    try {
      const res = await api.admin.bulkTaxCategory({ updates: pendingIds.map((serviceId) => ({ serviceId, taxCategory: pending[serviceId] })) });
      const names = (id: string): string => services.find((s) => s.id === id)?.name ?? id;
      const parts = [`Updated ${res.updated.length} ${res.updated.length === 1 ? 'service' : 'services'}`];
      if (res.unchanged.length > 0) parts.push(`${res.unchanged.length} already had that category`);
      if (res.errors.length > 0) parts.push(`${res.errors.length} failed: ${res.errors.map((e) => `${names(e.serviceId)} (${e.message})`).join('; ')}`);
      setApplyResult({ text: `${parts.join(' · ')}.`, failed: res.errors.length > 0 });
      setAppliedCount((n) => { const next = n + res.updated.length; if (businessId) writeApplied(businessId, next); return next; });
      // Keep only the failed rows staged so the admin can see and retry them.
      const failed = new Set(res.errors.map((e) => e.serviceId));
      setPending((cur) => Object.fromEntries(Object.entries(cur).filter(([id]) => failed.has(id))));
      setSelected([]);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setApplyBusy(false);
    }
  };

  // ── completion ───────────────────────────────────────────────────────────────
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeBusy, setCompleteBusy] = useState(false);
  const [completeErr, setCompleteErr] = useState<string | null>(null);

  const submitComplete = async (): Promise<void> => {
    if (!businessId) return;
    setCompleteBusy(true);
    setCompleteErr(null);
    try {
      const res = await api.admin.completeTaxReview(businessId, { changedCount: appliedCount });
      setResult(`Review completed for ${brandDisplayName(res.business.name)} on ${formatIsoDay(res.completedAt.slice(0, 10))}${appliedCount > 0 ? ` after ${appliedCount} category ${appliedCount === 1 ? 'change' : 'changes'}` : ' with nothing changed'}.`);
      setCompleteOpen(false);
      setAppliedCount(0);
      if (businessId) writeApplied(businessId, 0);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setCompleteErr(apiErrorMessage(e));
    } finally {
      setCompleteBusy(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  const status = review?.status ?? null;
  const nothingChangedLabel = appliedCount === 0 && pendingIds.length === 0;

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-display tracking-tight">Tax review</h1>
          <p className="text-sm text-[var(--color-muted)]">
            What becomes taxable and when, every service by tax category, and the exemptions and registrations to fix. Nothing changes until you click.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {businesses.length > 1 ? (
            <label className="block">
              <span className="block text-xs text-[var(--color-muted)]">Brand</span>
              <select value={businessId} onChange={(e) => pickBrand(e.target.value)} className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]">
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>{brandDisplayName(b.name)}</option>
                ))}
              </select>
            </label>
          ) : null}
          <Button variant="secondary" onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</Button>
        </div>
      </div>

      {bizErr ? <Card><div className="p-2 text-sm text-[var(--tint-red-text)]">{bizErr}</div></Card> : null}
      {err ? <Card><div className="p-2 text-sm text-[var(--tint-red-text)]">{err}</div></Card> : null}
      {result ? (
        <div className={okBar}>
          <span>{result}</span>
          <PillButton onClick={() => setResult(null)}>Dismiss</PillButton>
        </div>
      ) : null}

      {loading && !review ? (
        <Card><div className="p-2 text-sm text-[var(--color-muted)]">Loading…</div></Card>
      ) : review && status ? (
        <>
          {/* ── Status + the completion ───────────────────────────────────────────── */}
          {/* Tinted so the state reads at a glance (owner, s231 fix pass): amber while a review is due, green once
              nothing is. A 'profile-upcoming' reason shows the soonest headline instead of its generic label. */}
          <Card className={status.due ? 'bg-[var(--tint-warn-bg)]' : 'bg-[var(--tint-green-bg-soft)]'}>
            <div className="flex flex-col gap-3 p-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{brandDisplayName(review.businessName)}</span>
                  {status.due ? <span className={warnChipOnTint}>Review due</span> : <span className={okChipOnTint}>Nothing due</span>}
                </div>
                {status.due ? (
                  <ul className="list-disc pl-5">
                    {status.reasons.map((r) => (
                      <li key={r}>
                        {r === 'profile-upcoming' && review.headlines.length > 0
                          ? `${review.headlines[0].sentence}${review.headlines.length > 1 ? ` (and ${review.headlines.length - 1} more below)` : ''}`
                          : TAX_REVIEW_REASON_LABELS[r]}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[var(--color-muted)]">Nothing is due for this brand today.</p>
                )}
                <div className="text-xs text-[var(--color-muted)]">
                  Evaluated on {formatIsoDay(review.asOf)} (the brand's calendar) · last completed {review.taxReviewCompletedAt ? formatIsoDay(review.taxReviewCompletedAt.slice(0, 10)) : 'never'}
                  {status.decemberWindow ? ` · December window ${formatIsoDay(status.decemberWindow.from)} to ${formatIsoDay(status.decemberWindow.to)}` : ''}
                </div>
              </div>
              <div className="flex flex-col items-start gap-1 sm:items-end">
                {/* Completion is offered only while it means something (owner, s231 fix pass): a review is due, or
                    categories changed this visit. A completed brand with nothing changed has nothing to confirm. */}
                <Button onClick={() => { setCompleteErr(null); setCompleteOpen(true); }} disabled={pendingIds.length > 0 || applyBusy || (!status.due && appliedCount === 0)}>
                  {nothingChangedLabel ? 'Confirm all unchanged' : 'Complete review'}
                </Button>
                <span className="max-w-xs text-right text-xs text-[var(--color-muted)]">
                  {pendingIds.length > 0 ? (
                    'Apply or discard the staged changes first.'
                  ) : appliedCount > 0 ? (
                    <>
                      <span className="font-semibold text-[var(--tint-warn-text)]">
                        {appliedCount} category {appliedCount === 1 ? 'change' : 'changes'} applied this visit.
                      </span>{' '}
                      {status.due ? 'Complete the review to record them.' : 'The changes are saved; completing again records them on the review.'}
                    </>
                  ) : status.due ? (
                    'Nothing changed this visit: completing confirms every item as reviewed.'
                  ) : (
                    `Completed ${review.taxReviewCompletedAt ? formatIsoDay(review.taxReviewCompletedAt.slice(0, 10)) : ''}. The next review opens on December 1, or when a tax rule changes.`
                  )}
                </span>
              </div>
            </div>
          </Card>

          {/* ── What changes, in plain words ──────────────────────────────────────── */}
          <section className="space-y-2">
            <h2 className={sectionTitle}>What changes</h2>
            <Card>
              <div className="space-y-2 p-2 text-sm">
                {review.headlines.length === 0 ? (
                  <p className="text-[var(--color-muted)]">No tax rule or rate changes are scheduled within the next thirteen months in the loaded state profiles.</p>
                ) : (
                  review.headlines.map((h) => (
                    <div key={`${h.profileId}-${h.kind}-${h.category ?? h.jurisdictionId}-${h.effectiveFrom}`} className="flex flex-wrap items-baseline gap-2">
                      <span>{h.sentence}</span>
                      {h.kind === 'rule' && h.category && h.itemCount > 0 ? (
                        <a className="text-xs underline decoration-dotted underline-offset-2 hover:decoration-solid" href={`#cat-${h.category}`}>See them</a>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </Card>
          </section>

          {/* ── Registrations (02i T3.4; owner ruling s227 B2: none saved = collects nowhere) ─ */}
          <section className="space-y-2">
            <h2 className={sectionTitle}>Where this brand collects</h2>
            <Card>
              <div className="space-y-2 p-2 text-sm">
                {review.registrations.mustFix ? (
                  <p className="text-[var(--tint-warn-text)]">
                    <span className="font-semibold">No tax registrations saved.</span> Until one is saved, no sales tax is collected anywhere for this brand, and each invoice says so in a footnote (the client owes use tax to their state). Add one under{' '}
                    <Link className="underline decoration-dotted underline-offset-2 hover:decoration-solid" to="/admin/settings">Settings, Sales tax</Link> (California for each brand before go-live).
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {review.registrations.saved.map((r, i) => {
                      const live = review.registrations.inForce.some((x) => x.profileId === r.profileId && x.effectiveFrom === r.effectiveFrom && (x.effectiveTo ?? null) === (r.effectiveTo ?? null));
                      return (
                        <li key={`${r.profileId}-${r.effectiveFrom}-${i}`} className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{review.profiles.find((p) => p.id === r.profileId)?.stateName ?? r.profileId}</span>
                          <span className="text-[var(--color-muted)]">
                            {r.registrationNumber ? `permit ${r.registrationNumber} · ` : ''}from {formatIsoDay(r.effectiveFrom)}{r.effectiveTo ? ` to ${formatIsoDay(r.effectiveTo)}` : ''}
                          </span>
                          <span className={live ? mutedChip : warnChip}>{live ? 'In force' : 'Not in force today'}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {review.registrations.destinations.length > 0 ? (
                  <div className="border-t border-[rgb(var(--edge-rgb)/0.05)] pt-2 text-xs text-[var(--color-muted)]">
                    Where your clients are billed:{' '}
                    {review.registrations.destinations.map((d) => `${d.stateName} (${d.clientCount} ${d.clientCount === 1 ? 'client' : 'clients'}, ${d.registered ? 'registered' : 'not registered'})`).join(' · ')}
                  </div>
                ) : null}
              </div>
            </Card>
          </section>

          {/* ── Services by category (search, bulk select, set-category) ─────────── */}
          <section className="space-y-2">
            <h2 className={sectionTitle}>
              Services by tax category · {review.counts.activeServices} active
              {review.counts.uncategorised > 0 ? ` · ${review.counts.uncategorised} not set` : ''}
              {review.counts.inactiveServices > 0 ? ` · ${review.counts.inactiveServices} inactive not listed` : ''}
              {review.counts.otherBrandServices > 0 ? ` · ${review.counts.otherBrandServices} for other brands only` : ''}
            </h2>
            {applyResult ? (
              <div className={applyResult.failed ? warnBar : okBar}>
                <span>{applyResult.text}</span>
                <PillButton onClick={() => setApplyResult(null)}>Dismiss</PillButton>
              </div>
            ) : null}
            <Card>
              <div className="space-y-3 p-2">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <label className="flex flex-col gap-1 lg:col-span-1">
                    <span className="text-xs text-[var(--color-muted)]">Search</span>
                    <input className={inputClass} value={search} placeholder="Name, vendor, group or category" onChange={(e) => setSearch(e.target.value)} />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs text-[var(--color-muted)]">
                      Set selected to{selected.length > 0 ? ` · ${selected.length} selected` : ''}
                    </span>
                    <div className="flex items-center gap-2">
                      <select className={inputClass} value={bulkTarget} onChange={(e) => setBulkTarget(e.target.value as '' | CatalogTaxCategory)}>
                        <option value="">Choose a category</option>
                        {TAX_CATEGORY_OPTIONS.map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                      {/* The label never changes, so the button never changes size (the count sits in the field label). */}
                      <div className="shrink-0 whitespace-nowrap">
                        <Button variant="secondary" disabled={!bulkTarget || selected.length === 0} onClick={() => { if (bulkTarget) stage(selected, bulkTarget); setSelected([]); }}>
                          Stage
                        </Button>
                      </div>
                    </div>
                  </label>
                  <div className="flex flex-col items-center justify-end gap-2">
                    <div className="flex flex-wrap justify-center gap-2">
                      <PillButton onClick={selectShown} disabled={shown.length === 0}>Select all shown</PillButton>
                      <PillButton onClick={clearSelected} disabled={selected.length === 0}>Clear selection</PillButton>
                    </div>
                    <PillButton
                      onClick={() => setPending((cur) => { const next = { ...cur }; for (const s of suggestible) if (s.suggestion && s.suggestion.category !== s.taxCategory) next[s.id] = s.suggestion.category; return next; })}
                      disabled={suggestible.length === 0}
                    >
                      Use every suggestion ({suggestible.length})
                    </PillButton>
                  </div>
                </div>
                {pendingIds.length > 0 ? (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-[var(--tint-warn-bg)] px-3 py-2 text-sm">
                    <span className="text-[var(--tint-warn-text)]">
                      {pendingIds.length} staged {pendingIds.length === 1 ? 'change' : 'changes'}, not saved yet.
                    </span>
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" onClick={() => setPending({})} disabled={applyBusy}>Discard</Button>
                      <Button onClick={() => void applyChanges()} disabled={applyBusy}>{applyBusy ? 'Applying…' : `Apply ${pendingIds.length} ${pendingIds.length === 1 ? 'change' : 'changes'}`}</Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </Card>

            {services.length === 0 ? (
              <Card><div className="p-6 text-center text-sm text-[var(--color-muted)]">No active services are available to this brand.</div></Card>
            ) : shown.length === 0 ? (
              <Card><div className="p-6 text-center text-sm text-[var(--color-muted)]">No services match the search.</div></Card>
            ) : (
              CATALOG_TAX_CATEGORIES.map((cat) => {
                const rows = grouped.get(cat) ?? [];
                if (rows.length === 0) return null;
                return (
                  <Card key={cat}>
                    <div className="space-y-2 p-2" id={`cat-${cat}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="font-semibold">{taxCategoryLabel(cat)} <span className="text-xs font-normal text-[var(--color-muted)]">· {rows.length}</span></div>
                        <PillButton onClick={() => setSelected((l) => Array.from(new Set([...l, ...rows.map((r) => r.id)])))}>Select this group</PillButton>
                      </div>
                      <div className="divide-y divide-[rgb(var(--edge-rgb)/0.05)]">
                        {rows.map((s) => {
                          const staged = stagedOf(s);
                          const isPending = pending[s.id] !== undefined;
                          const suggest = s.suggestion && s.suggestion.category !== staged ? s.suggestion : null;
                          return (
                            <div key={s.id} className="flex flex-wrap items-start gap-3 py-2 text-sm">
                              <input type="checkbox" className="mt-1" checked={selected.includes(s.id)} onChange={() => toggleSelected(s.id)} aria-label={`Select ${s.name}`} />
                              <div className="min-w-0 flex-1 space-y-0.5">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium">{s.name}</span>
                                  <span className="text-xs text-[var(--color-muted)]">{s.shortName} · {s.vendor} · {s.category}{s.assignedClientCount > 0 ? ` · ${s.assignedClientCount} ${s.assignedClientCount === 1 ? 'client' : 'clients'}` : ''}</span>
                                  {s.taxCategory === null && !isPending ? <span className={warnChip}>Not set: counts as a professional service</span> : null}
                                  {isPending ? <span className={warnChip}>→ {taxCategoryLabel(pending[s.id])} (staged)</span> : null}
                                </div>
                                {suggest ? (
                                  <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted)]">
                                    <span>Suggested from the name: <strong>{taxCategoryLabel(suggest.category)}</strong> (matched {suggest.why}).</span>
                                    <PillButton size="xs" onClick={() => stage([s.id], suggest.category)}>Use suggestion</PillButton>
                                  </div>
                                ) : null}
                              </div>
                              <div className="flex items-center gap-2">
                                <select
                                  className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-2 py-1 text-xs"
                                  value={staged ?? ''}
                                  onChange={(e) => (e.target.value ? stage([s.id], e.target.value as CatalogTaxCategory) : unstage(s.id))}
                                  aria-label={`Tax category for ${s.name}`}
                                >
                                  <option value="" disabled>Not set</option>
                                  {TAX_CATEGORY_OPTIONS.map(([v, l]) => (
                                    <option key={v} value={v}>{l}</option>
                                  ))}
                                </select>
                                {isPending ? <PillButton size="xs" onClick={() => unstage(s.id)}>Undo</PillButton> : null}
                                <Link className="text-xs underline decoration-dotted underline-offset-2 hover:decoration-solid" to="/admin/services">Catalog</Link>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </section>

          {/* ── Exemptions (02i T3.5 / T4) ────────────────────────────────────────── */}
          <section className="space-y-2">
            <h2 className={sectionTitle}>Exemption certificates to fix</h2>
            <Card>
              <div className="p-2 text-sm">
                {review.exemptions.length === 0 ? (
                  <p className="text-[var(--color-muted)]">No client exemption is missing a certificate reference or expiring within 90 days.</p>
                ) : (
                  <ul className="space-y-1">
                    {review.exemptions.map((e) => (
                      <li key={e.clientId} className="flex flex-wrap items-center gap-2">
                        <Link className="font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid" to="/admin/clients">{e.clientName}</Link>
                        <span className="text-[var(--color-muted)]">{EXEMPTION_REASON_LABEL[e.reason] ?? e.reason}{e.certificateRef ? ` · certificate ${e.certificateRef}` : ''} · from {formatIsoDay(e.effectiveFrom)}{e.expiresAt ? ` to ${formatIsoDay(e.expiresAt)}` : ''}</span>
                        <span className={e.problem === 'not-yet-effective' ? mutedChip : warnChip}>{EXEMPTION_PROBLEM_LABEL[e.problem]}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
          </section>

          {/* ── Addresses that resolve to the fallback (s229 Q2 (A)) ───────────────── */}
          <section className="space-y-2">
            <h2 className={sectionTitle}>Clients whose address resolves to no jurisdiction</h2>
            <Card>
              <div className="space-y-2 p-2 text-sm">
                {review.addressIssues.length === 0 ? (
                  <p className="text-[var(--color-muted)]">Every client's billing address resolves to a loaded state profile.</p>
                ) : (
                  <ul className="space-y-1">
                    {review.addressIssues.map((a) => (
                      <li key={a.clientId} className="flex flex-wrap items-center gap-2">
                        <Link className="font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid" to="/admin/clients">{a.clientName}</Link>
                        {a.issue === 'no-address' ? (
                          <span className="text-[var(--color-muted)]">No address: billed at the brand's backup jurisdiction ({a.fallbackLabel}) and taxed there once the brand is registered.</span>
                        ) : (
                          <span className="text-[var(--color-muted)]">Address names {a.region}, which no loaded profile covers: nothing is collected (the client may owe use tax); the invoice rate still shows {a.fallbackLabel}.</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-[var(--color-muted)]">A mistyped state is the risk here: fix the address on the client and the lines resolve to the right profile on the next draft.</p>
              </div>
            </Card>
          </section>

          {/* ── The state profiles in three groups (02i T4) ─────────────────────────── */}
          <section className="space-y-2">
            <h2 className={sectionTitle}>State profiles</h2>
            {review.profiles.length === 0 ? (
              <Card><div className="p-2 text-sm text-[var(--tint-red-text)]">No tax profiles are loaded: every rate falls back to the brand default. This is a deployment problem, not a review item.</div></Card>
            ) : (
              review.profiles.map((p) => <ProfileCard key={p.id} p={p} />)
            )}
            {review.brandRules.length > 0 || review.brandRates.length > 0 ? (
              <Card>
                <div className="space-y-1 p-2 text-sm">
                  <div className="font-semibold">This brand's own rules <InfoTip text="Rows the brand keeps over the platform profile; a brand row wins for its state and category. Edited under Settings, Sales tax; shown here so the review reads the rules that actually apply." /></div>
                  {review.brandRules.map((r, i) => (
                    <div key={`r-${i}`}>{r.profileId} · {taxCategoryLabel(r.category)}: {r.taxable ? `taxable${r.taxableFraction !== undefined && r.taxableFraction < 1 ? ` on ${Math.round(r.taxableFraction * 100)}%` : ''}${r.rate !== undefined ? ` at ${pct(r.rate)}` : ''}` : 'not taxable'}{r.effectiveFrom ? ` from ${formatIsoDay(r.effectiveFrom)}` : ''}{r.effectiveTo ? ` through ${formatIsoDay(r.effectiveTo)}` : ''}</div>
                  ))}
                  {review.brandRates.map((r, i) => (
                    <div key={`t-${i}`}>{r.profileId} · {r.jurisdictionId}: {pct(r.rate)} from {formatIsoDay(r.effectiveFrom)}</div>
                  ))}
                </div>
              </Card>
            ) : null}
          </section>
        </>
      ) : null}

      {/* ── Complete review ──────────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={completeOpen}
        title={nothingChangedLabel ? 'Confirm all unchanged' : 'Complete the tax review'}
        confirmLabel={completeBusy ? 'Completing…' : nothingChangedLabel ? 'Confirm all unchanged' : 'Complete review'}
        busy={completeBusy}
        onConfirm={() => void submitComplete()}
        onCancel={() => setCompleteOpen(false)}
      >
        {review ? (
          <div className="space-y-2 text-sm">
            <p>
              This records that you reviewed {brandDisplayName(review.businessName)}'s tax categories on {formatIsoDay(review.asOf)}{appliedCount > 0 ? ` after ${appliedCount} category ${appliedCount === 1 ? 'change' : 'changes'}` : ', changing nothing'}. One audit row is written; nothing else changes.
            </p>
            <ul className="list-disc pl-5 text-[var(--color-muted)]">
              <li>{review.counts.activeServices} active services reviewed{review.counts.uncategorised > 0 ? `, ${review.counts.uncategorised} still not set (they count as professional services)` : ''}</li>
              <li>{review.exemptions.length === 0 ? 'No exemption certificate problems' : `${review.exemptions.length} exemption ${review.exemptions.length === 1 ? 'certificate' : 'certificates'} flagged`}</li>
              <li>{review.addressIssues.length === 0 ? 'Every address resolves' : `${review.addressIssues.length} ${review.addressIssues.length === 1 ? 'client address resolves' : 'client addresses resolve'} to the fallback`}</li>
              <li>{review.registrations.mustFix ? 'No tax registrations saved: this brand collects nowhere until one is' : `${review.registrations.saved.length} ${review.registrations.saved.length === 1 ? 'registration' : 'registrations'} saved`}</li>
            </ul>
            {completeErr ? <p className="text-[var(--tint-red-text)]">{completeErr}</p> : null}
          </div>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
